<template>
  <div class="page-content-wrapper">
    <div class="card rounded-3 border-1 shadow">
      <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center me-1 mt-2 ms-2">
        <h2 class="text-s fw-bold mb-0">Assets</h2>
        <div class="d-flex align-items-center gap-3">
          <button
            v-if="user_role === 'ClientAdmin'"
            type="button"
            class="btn btn-dark btn-sm text-xs rounded-3 fw-bold shadow"
            data-bs-toggle="modal"
            data-bs-target="#create_asset_modal"
          >
            <i class="fas fa-plus fw-bold"></i> New Asset
          </button>

          <!-- Search Bar -->
          <SearchBar
            v-model="search_query_assets"
            placeholder="Search assets..."
            label="Search"
            @update:modelValue="filterAssets"
          />
        </div>
      </div>

      <div class="card-body">
        <!-- Loading Spinner -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- Assets Section with Categories -->
        <div v-else class="assets-section">
          <!-- Categories with Assets Grid -->
          <div v-for="category in asset_categories" :key="category.id">
            <div class="category-container" v-if="category.assets.length > 0">
              <!-- Category Header -->
              <div class="category-header">
                <span class="category-name">{{ category.name }}</span>
              </div>
              <transition-group name="slide-up-fade" tag="div" class="assets-grid">
                <AssetCard
                  v-for="asset in category.assets"
                  :key="asset.id"
                  :asset="asset"
                  @view="view_asset(asset.id)"
                  @toggle="handleAssetToggle"
                  @assign="handleAssetAssign"
                  @add-customer="handleAddCustomer"
                  @on-behalf-application="handleOnBehalfApplication"
                />
              </transition-group>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="filtered_assets.length === 0 && !loading" class="empty-state-container">
            <i class="fas fa-inbox fa-4x text-muted mb-3" v-if="!search_query_assets"></i>
            <i class="fas fa-search fa-4x text-muted mb-3" v-else></i>
            <h5 class="text-muted">{{ search_query_assets ? 'No Results Found' : 'No Assets Available' }}</h5>
            <p class="text-muted">
              {{ search_query_assets ? `No assets found matching "${search_query_assets}"` : 'You don\'t have any assets yet. Create your first asset!' }}
            </p>
            <button
              v-if="!search_query_assets"
              type="button"
              class="btn bg-green text-dark rounded-3 mt-2"
              data-bs-toggle="modal"
              data-bs-target="#create_asset_modal"
            >
              <i class="fas fa-plus"></i> New Asset
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal fade"
      id="create_asset_modal"
      tabindex="-1"
      aria-labelledby="create_asset_modal_label"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="create_asset_modal_label">
              <strong>Create Asset</strong>
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="create_asset">
              <div class="mb-3">
                <label class="form-label fw-bold"
                  ><small>Asset name </small></label
                >
                <input
                  v-model="asset_name"
                  type="text"
                  class="form-control text-xs"
                  placeholder="name"
                  name="name"
                  required
                />
                <div class="name-message d-none">Asset name is required</div>
              </div>

              <div class="mb-3">
                <label class="form-label fw-bold"
                  ><small>Description</small></label
                >
                <input
                  v-model="asset_description"
                  type="text"
                  class="form-control text-xs"
                  placeholder="Description"
                  name="description"
                  required
                />
                <div class="description-message d-none">
                  Description is required
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label fw-bold"
                  ><small>Asset Type</small></label
                >
                <select
                  v-model="asset_type"
                  class="form-select text-xs"
                  name="asset_type"
                  required
                >
                  <option value="" disabled>Select Asset Type</option>
                  <option
                    v-for="type in asset_types"
                    :key="type.id"
                    :value="type.id"
                  >
                    {{ type.asset_type }}
                  </option>
                </select>
                <div class="type-message d-none">Asset type is required</div>
              </div>

              <div class="card-img-top">
                <div>
                  <img
                    v-if="image_url"
                    :src="image_url"
                    alt="Preview"
                    class="img-fluid rounded"
                  />
                  <div v-else class="placeholder">No image selected</div>
                </div>
              </div>

              <div class="d-flex justify-content-between mt-4">
                <div>
                  <button
                    type="button"
                    class="btn bg-green text-xs fw-bold rounded-small shadow"
                    @click="trigger_file_upload"
                  >
                    <i class="fa fa-upload"></i> Upload
                  </button>
                  <input
                    id="file-upload"
                    type="file"
                    accept="image/*"
                    @change="handle_file_upload"
                    ref="fileInput"
                    class="d-none text-xs upload-input"
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    class="btn bg-green text-xs fw-bold rounded-small shadow"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Assign asset to workflow Modal -->
  <div
    class="modal fade"
    id="assign_asset_to_workflow_modal"
    tabindex="-1"
    aria-labelledby="assign_asset_to_workflow_modal_Label"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="assign_asset_to_workflow_modal_Label">
            <strong>Assign to workflow</strong>
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <form
            @submit.prevent="
              assign_to_workflow(selected_asset, selected_workflow_id)
            "
          >
            <div class="mb-3">
              <label class="form-label fw-bold"><small>Workflow</small></label>

              <!-- Searchable dropdown -->
              <div class="searchable-select" ref="searchable_select">
                <div class="input-group">
                  <input
                    v-model="search_query"
                    type="text"
                    class="form-control form-control-sm text-xs"
                    placeholder="Click to select or type to search..."
                    @click="show_dropdown = true"
                    @focus="show_dropdown = true"
                    @blur="handle_blur"
                    @keydown.down.prevent="navigate_down"
                    @keydown.up.prevent="navigate_up"
                    @keydown.enter.prevent="select_highlighted"
                    @keydown.escape="show_dropdown = false"
                  />
                </div>

                <!-- Dropdown list -->
                <div
                  v-show="show_dropdown"
                  class="workflow-dropdown"
                  @mousedown.prevent
                >
                  <div class="dropdown-search-results">
                    <a
                      v-for="(workflow, index) in filtered_workflows"
                      :key="workflow.id"
                      href="#"
                      class="dropdown-item text-xs"
                      :class="{
                        active: workflow.id === selected_workflow_id,
                        highlighted: index === highlighted_index,
                      }"
                      @click.prevent="select_workflow(workflow)"
                      @mouseenter="highlighted_index = index"
                    >
                      <i
                        v-if="workflow.id === selected_workflow_id"
                        class="fas fa-check me-2"
                      ></i>
                      {{ workflow.workflow_name }}
                    </a>

                    <div
                      v-if="filtered_workflows.length === 0"
                      class="dropdown-item-text text-xs text-muted p-2"
                    >
                      No workflows found matching "{{ search_query }}"
                    </div>
                  </div>
                </div>
              </div>

              <small v-if="selected_workflow_id" class="text-success">
                <i class="fas fa-check-circle"></i> Selected:
                {{ selected_workflow_name }}
              </small>
            </div>

            <div class="modal-footer">
              <button
                type="button"
                class="btn btn-secondary btn-sm text-xs fw-bold rounded-pill"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="btn bg-green btn-sm text-xs fw-bold rounded-pill"
                :disabled="!selected_workflow_id"
              >
                Assign
              </button>
            </div>
          </form> 
        </div>
      </div>
    </div>
  </div>

  <!-- Send workflow to customer Modal -->
  <div
    class="modal fade"
    id="add_customer_to_asset_modal"
    tabindex="-1"
    aria-labelledby="add_customer_to_asset_modal_Label"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow-lg">
        <div class="modal-header border-0 pb-0">
          <h5 class="modal-title fw-bold" id="add_customer_to_asset_modal_Label">
            {{ customer_step === 'select' ? 'Send workflow to customer' : (customer_type === 'company' ? 'Send workflow to customer' : 'Send workflow to customer') }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="reset_customer_modal"
          ></button>
        </div>
        <div class="modal-body pt-2">
          <!-- Selected Asset Badge -->
          <div class="selected-asset-badge mb-3">
            <i class="fas fa-cube me-2 text-primary"></i>
            <span class="text-muted">Asset:</span>
            <strong class="ms-1">{{ selected_asset ? selected_asset.asset_name : 'None' }}</strong>
          </div>

          <!-- Step 1: Customer Type Selection -->
          <transition name="slide-fade" mode="out-in">
            <div v-if="customer_step === 'select'" key="select" class="customer-type-selection">
              <p class="text-muted text-sm mb-3">What type of customer would you like to add?</p>
              
              <div 
                class="customer-type-cards"
                :disabled="!customer_type"
                @click="customer_step = 'details'"
              >
                <!-- Company Option -->
                <div 
                  class="customer-type-card"
                  :class="{ 'selected': customer_type === 'company' }"
                  @click="customer_type = 'company'"
                >
                  <div class="card-icon">
                    <i class="fas fa-building"></i>
                  </div>
                  <div class="card-content">
                    <h6 class="mb-1 fw-semibold">Company</h6>
                    <p class="text-muted text-xs mb-0">Add a business or organization</p>
                  </div>
                  <div class="card-radio">
                    <div class="radio-circle" :class="{ 'checked': customer_type === 'company' }"></div>
                  </div>
                </div>

                <!-- Individual Option -->
                <div 
                  class="customer-type-card"
                  :class="{ 'selected': customer_type === 'individual' }"
                  @click="customer_type = 'individual'"
                >
                  <div class="card-icon">
                    <i class="fas fa-user"></i>
                  </div>
                  <div class="card-content">
                    <h6 class="mb-1 fw-semibold">Individual</h6>
                    <p class="text-muted text-xs mb-0">Add a person</p>
                  </div>
                  <div class="card-radio">
                    <div class="radio-circle" :class="{ 'checked': customer_type === 'individual' }"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 2: Customer Details Form -->
            <div v-else-if="customer_step === 'details'" key="details" class="customer-details-form">
              <button 
                type="button" 
                class="btn btn-link text-muted p-0 mb-3 text-decoration-none"
                @click="customer_step = 'select'"
              >
                <i class="fas fa-arrow-left me-2"></i>Back to selection
              </button>

              <form @submit.prevent="add_customer_to_asset">
                <!-- Company Fields: match Individual layout but replace ID Number with Registration Number -->
                <template v-if="customer_type === 'company'">
                  <div class="row">
                    <div class="col-6 mb-3">
                      <label class="form-label fw-semibold text-sm">
                        Company Name <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        v-model="company_name"
                        class="form-control"
                        placeholder="Company name"
                        required
                      />
                    </div>
                    <div class="col-6 mb-3">
                      <label class="form-label fw-semibold text-sm">
                        Registration Number <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        v-model="company_registration_number"
                        @input="formatRegistrationNumber"
                        class="form-control"
                        placeholder="YYYY/NNNNNN/NN"
                        maxlength="14"
                        required
                      />
                      <small class="text-muted">Format: YYYY/NNNNNN/NN</small>
                    </div>
                  </div>


                  <div class="mb-3">
                    <label class="form-label fw-semibold text-sm">
                      Email <span class="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      v-model="customer_email"
                      class="form-control"
                      placeholder="company@email.com"
                      required
                    />
                    <div class="customer-email-error text-danger text-xs mt-1 d-none"></div>
                  </div>

                  <div class="mb-3 phone-input-container">
                    <label class="form-label fw-semibold text-sm">
                      Phone <span class="text-danger">*</span>
                    </label>
                    <input
                      ref="customerPhoneInput"
                      type="tel"
                      v-model="customer_phone_number"
                      class="form-control"
                      :class="{ 'is-invalid': phone_number_error }"
                      placeholder="Enter phone number"
                      required
                    />
                    <div class="customer-phone-error text-danger text-xs mt-1 d-none"></div>
                  </div>
                </template>

                <!-- Individual Fields -->
                <template v-else>
                  <div class="row">
                    <div class="col-6 mb-3">
                      <label class="form-label fw-semibold text-sm">
                        First Name <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        v-model="customer_first_name"
                        class="form-control"
                        placeholder="First name"
                        required
                      />
                    </div>
                    <div class="col-6 mb-3">
                      <label class="form-label fw-semibold text-sm">
                        Last Name <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        v-model="customer_last_name"
                        class="form-control"
                        placeholder="Last name"
                        required
                      />
                    </div>
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-semibold text-sm">
                      ID Number <span class="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      v-model="customer_id_number"
                      class="form-control"
                      placeholder="Enter ID number"
                      maxlength="13"
                      required
                    />
                    <div class="customer-id-error text-danger text-xs mt-1 d-none"></div>
                  </div>

                  <div class="mb-3">
                    <label class="form-label fw-semibold text-sm">
                      Email <span class="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      v-model="customer_email"
                      class="form-control"
                      placeholder="email@example.com"
                      required
                    />
                    <div class="customer-email-error text-danger text-xs mt-1 d-none"></div>
                  </div>

                  <div class="mb-3 phone-input-container">
                    <label class="form-label fw-semibold text-sm">
                      Phone Number <span class="text-danger">*</span>
                    </label>
                    <input
                      ref="customerPhoneInput"
                      type="tel"
                      v-model="customer_phone_number"
                      class="form-control"
                      :class="{ 'is-invalid': phone_number_error }"
                      placeholder="Enter phone number"
                      required
                    />
                    <div class="customer-phone-error text-danger text-xs mt-1 d-none"></div>
                  </div>
                </template>

                <div class="alert alert-light border text-xs py-2 px-3 mb-3">
                  <i class="fas fa-info-circle text-primary me-2"></i>
                  A temporary password will be sent to the customer's email.
                </div>

                <div class="d-flex gap-2">
                  <button
                    type="button"
                    class="btn btn-secondary btn-sm text-xs fw-bold rounded-pill flex-grow-1 py-2"
                    data-bs-dismiss="modal"
                    @click="reset_customer_modal"
                  >
                    <i class="fas fa-times me-2"></i>Cancel
                  </button>
                  <button
                    type="submit"
                    class="btn btn-sm text-xs fw-bold rounded-pill flex-grow-1 py-2"
                    style="background-color: var(--primary-color); color: #000;"
                  >
                    <i class="fas fa-paper-plane me-2"></i>Send workflow
                  </button>
                </div>
              </form>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
  <!-- Asset Info Modal -->
  <AssetInfoModal 
    v-if="showAssetModal" 
    :assetId="selectedAssetId" 
    @close="closeAssetModal" 
  />
</template>
<script src="./create_asset.js"></script>

<style scoped>
.card-body {
  padding: 20px;
}

.assets-section {
  padding: 4px 0;
  margin-bottom: -32px;
}

.category-container {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-top: -12px;
  margin-bottom: 36px;
  border: 1px solid rgba(108, 117, 125, 0.15);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  position: relative;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  position: absolute;
  top: -12px;
  left: 24px;
  right: 24px;
  z-index: 2;
}

.category-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6c757d;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: white;
  padding: 0 12px;
}

.search-input-wrapper {
  position: relative;
  width: 300px;
  margin-top: 4px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 0.875rem;
  pointer-events: none;
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 8px 36px 8px 36px;
  border: 1px solid rgba(108, 117, 125, 0.25);
  border-radius: 20px;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.search-input:focus {
  outline: none;
  background: white;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb, 89, 255, 116), 0.1);
}

.search-input::placeholder {
  color: #adb5bd;
  font-size: 0.875rem;
}

.clear-search {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 50%;
  transition: all 0.2s ease;
  font-size: 0.75rem;
  z-index: 1;
}

.clear-search:hover {
  background: rgba(108, 117, 125, 0.1);
  color: #495057;
}

.assets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

@media (max-width: 1400px) {
  .assets-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 14px;
  }
}

@media (max-width: 992px) {
  .assets-grid {
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 12px;
  }

  .search-input-wrapper {
    width: 250px;
  }
}

@media (max-width: 768px) {
  .assets-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px;
  }
  
  .search-input-wrapper {
    width: 100%;
    max-width: 250px;
  }
}

@media (max-width: 576px) {
  .assets-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  
  .search-input-wrapper {
    max-width: 100%;
  }
}

/* Empty State */
.text-center i {
  opacity: 0.3;
}

/* Add Customer Modal Styles */
.selected-asset-badge {
  display: inline-flex;
  align-items: center;
  background: #f8f9fa;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  border: 1px solid #e9ecef;
}

.customer-type-selection {
  animation: slideIn 0.3s ease-out;
}

.customer-type-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.customer-type-card {
  display: flex;
  align-items: center;
  padding: 16px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
}

.customer-type-card:hover {
  border-color: #dee2e6;
  background: #f8f9fa;
}

.customer-type-card.selected {
  border-color: var(--primary-color);
  background: rgba(89, 255, 116, 0.08);
}

.customer-type-card .card-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 10px;
  margin-right: 14px;
  font-size: 1.25rem;
  color: #6c757d;
  transition: all 0.2s ease;
}

.customer-type-card.selected .card-icon {
  background: var(--primary-color);
  color: #000;
}

.customer-type-card .card-content {
  flex: 1;
}

.customer-type-card .card-radio {
  margin-left: auto;
}

.radio-circle {
  width: 22px;
  height: 22px;
  border: 2px solid #dee2e6;
  border-radius: 50%;
  position: relative;
  transition: all 0.2s ease;
}

.radio-circle.checked {
  border-color: var(--primary-color);
}

.radio-circle.checked::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background: var(--primary-color);
  border-radius: 50%;
}

.customer-details-form {
  animation: slideIn 0.3s ease-out;
}

/* Phone input container styles for intl-tel-input */
.phone-input-container {
  position: relative;
}

.phone-input-container .iti {
  width: 100%;
}

.phone-input-container .iti__flag-container {
  z-index: 10;
}

.phone-input-container input.form-control {
  padding-left: 52px;
}

.phone-input-container input.form-control.is-invalid {
  border-color: #dc3545;
}

/* Slide transition */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from {
  transform: translateX(20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
